import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  CircularProgress,
} from "@mui/material";
import theme from "./theme/theme";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import WhatsAppButton from "./components/layout/WhatsAppButton";
import HomePage from "./pages/HomePage";

const AppointmentModal = lazy(() =>
  import("./components/appointment/AppointmentModal"),
);

// ── Lazy load all pages — only Home loads immediately ──
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));

// ── Minimal fallback shown during page chunk load ──
const PageLoader = () => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
    <CircularProgress size={36} thickness={3} sx={{ color: "primary.main" }} />
  </Box>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

function AppContent() {
  const [modalOpen, setModalOpen] = useState(() => {
    return !sessionStorage.getItem("modal_shown");
  });
  const [preselectedService, setPreselectedService] = useState("");
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (modalOpen) {
      sessionStorage.setItem("modal_shown", "1");
    }
  }, [modalOpen]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const openModal = useCallback((serviceId = "") => {
    setPreselectedService(serviceId);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setPreselectedService(""), 300);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <ScrollToTop />
      <Navbar onBookClick={openModal} />

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage onBookClick={openModal} />} />
            <Route
              path="/about"
              element={<AboutPage onBookClick={openModal} />}
            />
            <Route
              path="/services"
              element={<ServicesPage onBookClick={openModal} />}
            />
            <Route
              path="/reviews"
              element={<ReviewsPage onBookClick={openModal} />}
            />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </Suspense>
      </Box>

      <Footer onBookClick={openModal} />
      <WhatsAppButton />
      <Suspense fallback={null}>
        <AppointmentModal
          open={modalOpen}
          onClose={closeModal}
          preselectedService={preselectedService}
        />
      </Suspense>
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}
